import { isUtilityInteractionStateProp, isUtilityPropName, resolveUtilityPropName, UTILITY_CLASS_PREFIX_BY_PROP, UTILITY_INTERACTION_STATE_BY_PROP, } from './contract';
import { classifyBorderStyleUtilityValue, classifyBorderUtilityValue, classifyBorderWidthUtilityValue, } from './border-values';
import { classifyDimensionUtilityValue } from './dimension-values';
import { classifyRadiusUtilityValue } from './radius-values';
import { classifyShadowUtilityValue } from './shadow-values';
import { classifySpacingUtilityValue } from './spacing-values';
import { classifyZIndexUtilityValue } from './z-index-values';
import { formatArbitraryUtilityClassSuffix, formatBorderStyleUtilityClassSuffix, formatBorderUtilityClassSuffix, formatBorderWidthUtilityClassSuffix, formatDimensionUtilityClassSuffix, formatRadiusUtilityClassSuffix, formatShadowUtilityClassSuffix, formatSpacingUtilityClassSuffix, formatZIndexUtilityClassSuffix, } from './utility-class-suffix';
import { SEMANTIC_COLOR_TOKEN_KEYS } from '@smoothie-framework/ui-core/semantic';
import { isCssColorKeyword, normalizeColorUtilityClassSuffix, toKebabCase } from './color-values';
import { getBreakpointOrder, isBreakpointKey, listResponsiveConditions, resolveHideBelowCondition, resolveResponsiveCondition, toResponsiveValueRecord, } from './breakpoints';
const SIMPLE_CLASS_VALUE_PATTERN = /^[a-z0-9-]+$/i;
const POSITIVE_INTEGER_VALUE_PATTERN = /^[1-9]\d*$/;
const NUMERIC_SPACING_UTILITY_PROPS = new Set([
    'gap',
    'gapX',
    'gapY',
    'padding',
    'paddingX',
    'paddingY',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'paddingStart',
    'paddingEnd',
    'margin',
    'marginX',
    'marginY',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'marginStart',
    'marginEnd',
    'spaceX',
    'spaceY',
    'translateX',
    'translateY',
]);
const DIMENSION_UTILITY_PROPS = new Set([
    'width',
    'height',
    'minWidth',
    'maxWidth',
    'minHeight',
    'maxHeight',
    'basis',
]);
const BORDER_UTILITY_PROPS = new Set([
    'border',
    'borderTop',
    'borderBottom',
    'borderLeft',
    'borderRight',
    'borderStart',
    'borderEnd',
    'borderX',
    'borderY',
]);
const BORDER_WIDTH_UTILITY_PROPS = new Set([
    'borderWidth',
    'borderTopWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderRightWidth',
    'borderStartWidth',
    'borderEndWidth',
    'borderXWidth',
    'borderYWidth',
    'divideX',
    'divideY',
]);
const BORDER_STYLE_UTILITY_PROPS = new Set([
    'borderStyle',
    'borderTopStyle',
    'borderBottomStyle',
    'borderLeftStyle',
    'borderRightStyle',
    'borderStartStyle',
    'borderEndStyle',
    'divideStyle',
]);
const IMPLICIT_SOLID_BORDER_WIDTH_UTILITY_PROPS = new Set(BORDER_WIDTH_UTILITY_PROPS);
const IMPLICIT_SOLID_BORDER_STYLE_UTILITY_PROPS = new Set(BORDER_STYLE_UTILITY_PROPS);
const COLOR_UTILITY_PROPS = new Set([
    'background',
    'backgroundColor',
    'color',
    'borderColor',
    'borderTopColor',
    'borderBottomColor',
    'borderLeftColor',
    'borderRightColor',
    'borderStartColor',
    'borderEndColor',
    'borderXColor',
    'borderYColor',
    'divideColor',
    'outlineColor',
]);
const RADIUS_UTILITY_PROPS = new Set([
    'borderRadius',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'borderStartStartRadius',
    'borderStartEndRadius',
    'borderEndStartRadius',
    'borderEndEndRadius',
    'borderInlineStartRadius',
    'borderInlineEndRadius',
    'borderTopRadius',
    'borderBottomRadius',
    'borderLeftRadius',
    'borderRightRadius',
]);
const SHADOW_UTILITY_PROPS = new Set([
    'boxShadow',
]);
const Z_INDEX_UTILITY_PROPS = new Set([
    'zIndex',
]);
const SEMANTIC_COLOR_VALUE_SET = new Set(SEMANTIC_COLOR_TOKEN_KEYS);
const EMPTY_PROP_NAMES = new Set();
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function needsSemanticColorSuffixNormalization(value) {
    return /^[a-zA-Z][a-zA-Z0-9-]*$/.test(value) && toKebabCase(value) !== value;
}
function normalizeUtilityClassSuffix(propName, rawValue) {
    if (rawValue === undefined || rawValue === null) {
        return undefined;
    }
    const normalizedPropName = resolveUtilityPropName(propName);
    if (typeof rawValue === 'boolean') {
        if (normalizedPropName === 'inline') {
            return rawValue ? 'inline' : undefined;
        }
        if (normalizedPropName === 'wrap') {
            return rawValue ? 'wrap' : 'nowrap';
        }
        if (normalizedPropName === 'truncate') {
            return rawValue ? '' : 'none';
        }
        if (normalizedPropName === 'uppercase') {
            return rawValue ? '' : 'none';
        }
        return rawValue ? 'true' : 'false';
    }
    const value = String(rawValue).trim();
    if (value.length === 0) {
        return undefined;
    }
    if (NUMERIC_SPACING_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifySpacingUtilityValue(normalizedPropName, rawValue);
        return formatSpacingUtilityClassSuffix(classifiedValue);
    }
    if (DIMENSION_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifyDimensionUtilityValue(rawValue);
        return formatDimensionUtilityClassSuffix(classifiedValue);
    }
    if (BORDER_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifyBorderUtilityValue(rawValue);
        return formatBorderUtilityClassSuffix(classifiedValue);
    }
    if (BORDER_WIDTH_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifyBorderWidthUtilityValue(rawValue);
        return formatBorderWidthUtilityClassSuffix(classifiedValue);
    }
    if (BORDER_STYLE_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifyBorderStyleUtilityValue(rawValue);
        return formatBorderStyleUtilityClassSuffix(classifiedValue);
    }
    if (RADIUS_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifyRadiusUtilityValue(rawValue);
        return formatRadiusUtilityClassSuffix(classifiedValue);
    }
    if (SHADOW_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifyShadowUtilityValue(rawValue);
        return formatShadowUtilityClassSuffix(classifiedValue);
    }
    if (Z_INDEX_UTILITY_PROPS.has(normalizedPropName)) {
        const classifiedValue = classifyZIndexUtilityValue(rawValue);
        return formatZIndexUtilityClassSuffix(classifiedValue);
    }
    if (normalizedPropName === 'maxLines') {
        return POSITIVE_INTEGER_VALUE_PATTERN.test(value) ? value : undefined;
    }
    if (value === 'auto' && (normalizedPropName === 'margin' || normalizedPropName === 'marginX' || normalizedPropName === 'marginY')) {
        return value;
    }
    if (COLOR_UTILITY_PROPS.has(normalizedPropName) && SEMANTIC_COLOR_VALUE_SET.has(value)) {
        const classPrefix = UTILITY_CLASS_PREFIX_BY_PROP[normalizedPropName];
        return normalizeColorUtilityClassSuffix(classPrefix, value);
    }
    if (COLOR_UTILITY_PROPS.has(normalizedPropName)
        && needsSemanticColorSuffixNormalization(value)
        && !isCssColorKeyword(value)) {
        const classPrefix = UTILITY_CLASS_PREFIX_BY_PROP[normalizedPropName];
        return normalizeColorUtilityClassSuffix(classPrefix, value);
    }
    if (SIMPLE_CLASS_VALUE_PATTERN.test(value)) {
        return value;
    }
    return formatArbitraryUtilityClassSuffix(value);
}
export function wouldEmitUtilityClasses(propName, propValue, breakpointInput) {
    const normalizedPropName = resolveUtilityPropName(propName);
    if (normalizedPropName === 'hideFrom' || normalizedPropName === 'hideBelow') {
        return buildVisibilityUtilityClass(normalizedPropName, propValue, breakpointInput) !== undefined;
    }
    const responsiveValue = toResponsiveValueRecord(propValue, breakpointInput);
    if (responsiveValue) {
        return buildResponsiveUtilityClasses(normalizedPropName, responsiveValue, breakpointInput).length > 0;
    }
    if (isPlainObject(propValue)) {
        return (normalizedPropName === 'padding' || normalizedPropName === 'margin')
            && buildGroupedSpacingUtilityClasses(normalizedPropName, propValue).length > 0;
    }
    if (shouldOmitBaseUtilityClass(normalizedPropName, propValue)) {
        return false;
    }
    return buildUtilityClass(normalizedPropName, propValue) !== undefined;
}
export function buildUtilityClass(propName, value, breakpoint = 'base') {
    const normalizedPropName = resolveUtilityPropName(propName);
    const classPrefix = UTILITY_CLASS_PREFIX_BY_PROP[normalizedPropName];
    if (!classPrefix) {
        return undefined;
    }
    const classSuffix = normalizeUtilityClassSuffix(normalizedPropName, value);
    if (classSuffix === undefined) {
        return undefined;
    }
    const baseClass = classSuffix.length > 0 ? `smth-${classPrefix}-${classSuffix}` : `smth-${classPrefix}`;
    return breakpoint === 'base' ? baseClass : `${breakpoint}:${baseClass}`;
}
function buildResponsiveUtilityClasses(propName, value, breakpointInput) {
    const classes = [];
    const normalizedPropName = resolveUtilityPropName(propName);
    for (const breakpoint of listResponsiveConditions(value, breakpointInput)) {
        if (!Object.prototype.hasOwnProperty.call(value, breakpoint)) {
            continue;
        }
        if ((normalizedPropName === 'padding' || normalizedPropName === 'margin') && isPlainObject(value[breakpoint])) {
            classes.push(...buildGroupedSpacingUtilityClasses(normalizedPropName, value[breakpoint], breakpoint));
            continue;
        }
        if (shouldOmitBaseUtilityClass(normalizedPropName, value[breakpoint], breakpoint)) {
            continue;
        }
        const className = buildUtilityClass(normalizedPropName, value[breakpoint], breakpoint);
        if (className) {
            classes.push(className);
        }
    }
    return classes;
}
function getExplicitBreakpointValue(propValue, breakpoint, breakpointInput) {
    const responsiveValue = toResponsiveValueRecord(propValue, breakpointInput);
    if (responsiveValue) {
        return Object.prototype.hasOwnProperty.call(responsiveValue, breakpoint)
            ? responsiveValue[breakpoint]
            : undefined;
    }
    if (propValue === undefined || propValue === null) {
        return undefined;
    }
    return breakpoint === 'base' ? propValue : undefined;
}
function buildBackgroundAliasClasses(props, excludedPropNames, breakpointInput) {
    const background = excludedPropNames.has('background') ? undefined : props.background;
    const backgroundColor = excludedPropNames.has('backgroundColor') ? undefined : props.backgroundColor;
    if (background === undefined && backgroundColor === undefined) {
        return [];
    }
    const classes = [];
    const conditionNames = new Set(['base']);
    const responsiveBackground = toResponsiveValueRecord(background, breakpointInput);
    const responsiveBackgroundColor = toResponsiveValueRecord(backgroundColor, breakpointInput);
    for (const breakpoint of getBreakpointOrder(breakpointInput)) {
        conditionNames.add(breakpoint);
    }
    if (responsiveBackground) {
        for (const condition of listResponsiveConditions(responsiveBackground, breakpointInput)) {
            conditionNames.add(condition);
        }
    }
    if (responsiveBackgroundColor) {
        for (const condition of listResponsiveConditions(responsiveBackgroundColor, breakpointInput)) {
            conditionNames.add(condition);
        }
    }
    for (const breakpoint of conditionNames) {
        const resolvedBackgroundColor = getExplicitBreakpointValue(backgroundColor, breakpoint, breakpointInput);
        const resolvedBackground = getExplicitBreakpointValue(background, breakpoint, breakpointInput);
        const propName = resolvedBackgroundColor !== undefined
            ? 'backgroundColor'
            : resolvedBackground !== undefined
                ? 'background'
                : undefined;
        const propValue = resolvedBackgroundColor ?? resolvedBackground;
        if (!propName || propValue === undefined) {
            continue;
        }
        const className = buildUtilityClass(propName, propValue, breakpoint);
        if (className) {
            classes.push(className);
        }
    }
    return classes;
}
function buildGroupedSpacingUtilityClasses(propName, value, breakpoint = 'base') {
    const classes = [];
    const xPropName = propName === 'padding' ? 'paddingX' : 'marginX';
    const yPropName = propName === 'padding' ? 'paddingY' : 'marginY';
    const xClass = buildUtilityClass(xPropName, value.x, breakpoint);
    const yClass = buildUtilityClass(yPropName, value.y, breakpoint);
    if (xClass) {
        classes.push(xClass);
    }
    if (yClass) {
        classes.push(yClass);
    }
    return classes;
}
function buildVisibilityUtilityClass(propName, value, breakpointInput) {
    const condition = typeof value === 'string' && isBreakpointKey(value, breakpointInput)
        ? propName === 'hideFrom'
            ? value
            : resolveHideBelowCondition(value, breakpointInput)
        : undefined;
    return condition && resolveResponsiveCondition(condition, breakpointInput)?.mediaQuery
        ? buildUtilityClass(propName, value, condition)
        : undefined;
}
function prefixInteractionStateClassName(className, state) {
    const breakpointSeparatorIndex = className.indexOf(':smth-');
    if (breakpointSeparatorIndex === -1) {
        return `${state}:${className}`;
    }
    return `${className.slice(0, breakpointSeparatorIndex)}:${state}${className.slice(breakpointSeparatorIndex)}`;
}
function buildInteractionStateUtilityClasses(statePropName, propValue, _excludedPropNames, breakpointInput) {
    if (!isPlainObject(propValue)) {
        return [];
    }
    const state = UTILITY_INTERACTION_STATE_BY_PROP[statePropName];
    return buildManagedUtilityClasses(propValue, EMPTY_PROP_NAMES, breakpointInput)
        .map((className) => prefixInteractionStateClassName(className, state));
}
function collectUtilityConditions(propName, propValue, breakpointInput) {
    const normalizedPropName = resolveUtilityPropName(propName);
    const responsiveValue = toResponsiveValueRecord(propValue, breakpointInput);
    if (responsiveValue) {
        const conditions = [];
        for (const breakpoint of listResponsiveConditions(responsiveValue, breakpointInput)) {
            if (!Object.prototype.hasOwnProperty.call(responsiveValue, breakpoint)) {
                continue;
            }
            const value = responsiveValue[breakpoint];
            if (shouldOmitBaseUtilityClass(normalizedPropName, value, breakpoint)) {
                continue;
            }
            if (buildUtilityClass(normalizedPropName, value, breakpoint)) {
                conditions.push(breakpoint);
            }
        }
        return conditions;
    }
    if (shouldOmitBaseUtilityClass(normalizedPropName, propValue, 'base')) {
        return [];
    }
    return buildUtilityClass(normalizedPropName, propValue, 'base') ? ['base'] : [];
}
function orderResponsiveConditions(conditions, breakpointInput) {
    const remaining = new Set(conditions);
    const ordered = [];
    if (remaining.delete('base')) {
        ordered.push('base');
    }
    for (const breakpoint of getBreakpointOrder(breakpointInput)) {
        if (remaining.delete(breakpoint)) {
            ordered.push(breakpoint);
        }
    }
    ordered.push(...Array.from(remaining).sort());
    return ordered;
}
function hasExplicitBorderStyleAtCondition(targetCondition, explicitConditions, breakpointInput) {
    if (explicitConditions.has('base')) {
        return true;
    }
    if (targetCondition === 'base') {
        return false;
    }
    if (explicitConditions.has(targetCondition)) {
        return true;
    }
    const breakpointOrder = getBreakpointOrder(breakpointInput);
    const targetIndex = breakpointOrder.indexOf(targetCondition);
    if (targetIndex === -1) {
        return false;
    }
    for (let index = 0; index < targetIndex; index += 1) {
        if (explicitConditions.has(breakpointOrder[index])) {
            return true;
        }
    }
    return false;
}
function buildImplicitSolidBorderStyleClasses(props, excludedPropNames, breakpointInput) {
    let implicitConditions;
    let explicitConditions;
    for (const propName in props) {
        if (!Object.prototype.hasOwnProperty.call(props, propName)) {
            continue;
        }
        if (!isUtilityPropName(propName)) {
            continue;
        }
        const normalizedPropName = resolveUtilityPropName(propName);
        if (excludedPropNames.has(propName) || excludedPropNames.has(normalizedPropName)) {
            continue;
        }
        const hasImplicitStyle = IMPLICIT_SOLID_BORDER_WIDTH_UTILITY_PROPS.has(normalizedPropName);
        const hasExplicitStyle = IMPLICIT_SOLID_BORDER_STYLE_UTILITY_PROPS.has(normalizedPropName);
        if (!hasImplicitStyle && !hasExplicitStyle) {
            continue;
        }
        const propValue = props[propName];
        const conditions = collectUtilityConditions(normalizedPropName, propValue, breakpointInput);
        if (hasImplicitStyle) {
            implicitConditions ?? (implicitConditions = new Set());
            for (const condition of conditions) {
                implicitConditions.add(condition);
            }
        }
        if (hasExplicitStyle) {
            explicitConditions ?? (explicitConditions = new Set());
            for (const condition of conditions) {
                explicitConditions.add(condition);
            }
        }
    }
    if (!implicitConditions) {
        return [];
    }
    const classes = [];
    for (const condition of orderResponsiveConditions(implicitConditions, breakpointInput)) {
        if (hasExplicitBorderStyleAtCondition(condition, explicitConditions ?? EMPTY_PROP_NAMES, breakpointInput)) {
            continue;
        }
        const className = buildUtilityClass('borderStyle', 'solid', condition);
        if (className) {
            classes.push(className);
        }
    }
    return classes;
}
export function buildManagedUtilityClasses(props, excludedPropNames = EMPTY_PROP_NAMES, breakpointInput) {
    const classes = [];
    const handledPropNames = new Set();
    for (const propName in props) {
        if (!Object.prototype.hasOwnProperty.call(props, propName)) {
            continue;
        }
        const propValue = props[propName];
        if (isUtilityInteractionStateProp(propName)) {
            classes.push(...buildInteractionStateUtilityClasses(propName, propValue, excludedPropNames, breakpointInput));
            continue;
        }
        if (!isUtilityPropName(propName)) {
            continue;
        }
        const normalizedPropName = resolveUtilityPropName(propName);
        if (normalizedPropName !== propName
            && !excludedPropNames.has(normalizedPropName)
            && wouldEmitUtilityClasses(normalizedPropName, props[normalizedPropName], breakpointInput)) {
            continue;
        }
        if (handledPropNames.has(normalizedPropName)) {
            continue;
        }
        if (excludedPropNames.has(propName) || excludedPropNames.has(normalizedPropName)) {
            continue;
        }
        if (normalizedPropName === 'background' || normalizedPropName === 'backgroundColor') {
            classes.push(...buildBackgroundAliasClasses(props, excludedPropNames, breakpointInput));
            handledPropNames.add('background');
            handledPropNames.add('backgroundColor');
            continue;
        }
        if (normalizedPropName === 'hideFrom' || normalizedPropName === 'hideBelow') {
            const className = buildVisibilityUtilityClass(normalizedPropName, propValue, breakpointInput);
            if (className) {
                classes.push(className);
            }
            handledPropNames.add(normalizedPropName);
            continue;
        }
        const responsiveValue = toResponsiveValueRecord(propValue, breakpointInput);
        if (responsiveValue) {
            const responsiveClasses = buildResponsiveUtilityClasses(normalizedPropName, responsiveValue, breakpointInput);
            classes.push(...responsiveClasses);
            if (responsiveClasses.length > 0) {
                handledPropNames.add(normalizedPropName);
            }
            continue;
        }
        if (isPlainObject(propValue)) {
            if (normalizedPropName === 'padding' || normalizedPropName === 'margin') {
                const groupedClasses = buildGroupedSpacingUtilityClasses(normalizedPropName, propValue);
                classes.push(...groupedClasses);
                if (groupedClasses.length > 0) {
                    handledPropNames.add(normalizedPropName);
                }
            }
            continue;
        }
        if (shouldOmitBaseUtilityClass(normalizedPropName, propValue)) {
            continue;
        }
        const className = buildUtilityClass(normalizedPropName, propValue);
        if (className) {
            classes.push(className);
            handledPropNames.add(normalizedPropName);
        }
    }
    classes.push(...buildImplicitSolidBorderStyleClasses(props, excludedPropNames, breakpointInput));
    return classes;
}
function shouldOmitBaseUtilityClass(propName, propValue, breakpoint = 'base') {
    return propName === 'truncate'
        && breakpoint === 'base'
        && (propValue === false || propValue === 'false');
}
//# sourceMappingURL=classnames.js.map